import { CSSProperties, useRef } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import cn from 'classnames';
import { usePercentageTableColumns } from '../../../hooks/shared/usePercentageTableColumns';
import styles from './style.module.css';

const DEFAULT_LOADING_TIP = 'Загрузка...';

export type LoadingTableProps<RecordType extends object> =
  TableProps<RecordType> & {
    percentageColumnWidths?: boolean;
  };

type PercentageColumnWidthStyle = CSSProperties & {
  '--table-scrollbar-width': string;
};

const normalizeTableLoading = <RecordType extends object>(
  loading: TableProps<RecordType>['loading'],
): TableProps<RecordType>['loading'] => {
  if (loading === true) {
    return {
      spinning: true,
      tip: DEFAULT_LOADING_TIP,
    };
  }

  if (!loading) {
    return false;
  }

  return {
    tip: DEFAULT_LOADING_TIP,
    ...loading,
  };
};

export const LoadingTable = <RecordType extends object = object>({
  loading,
  className,
  columns,
  percentageColumnWidths = false,
  ...props
}: LoadingTableProps<RecordType>) => {
  const tableHostRef = useRef<HTMLDivElement | null>(null);
  const { columns: resolvedColumns, scrollbarWidth } =
    usePercentageTableColumns({
      columns,
      enabled: percentageColumnWidths,
      tableHostRef,
    });

  const table = (
    <Table<RecordType>
      {...props}
      columns={resolvedColumns}
      className={cn(
        styles.loadingTable,
        percentageColumnWidths && styles.percentageColumnWidths,
        className,
      )}
      loading={normalizeTableLoading(loading)}
    />
  );

  if (!percentageColumnWidths) {
    return table;
  }

  const tableHostStyle: PercentageColumnWidthStyle = {
    '--table-scrollbar-width': `${scrollbarWidth}px`,
  };

  return (
    <div
      ref={tableHostRef}
      className={styles.percentageColumnWidthsHost}
      style={tableHostStyle}
    >
      {table}
    </div>
  );
};
